import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { DSProvider } from "@toddle-edu/ds-web";
import classNames from "classnames";

import Loading from "UIComponents/Loading";

import { FilterActions, FilterList, FilterOptions } from "./components";
import { useFocusTrap } from "Hooks";

import classes from "./FiltersOverlay.scss";

// Note: Helper functions like `getAllFiltersCount` and `getAppliedFiltersCountForFilter`
// should be provided by the consumer based on their specific logic for comparing
// currentFilters against their defaults.

/**
 * @component FiltersOverlay
 * @description A modal overlay component for selecting and applying filters.
 * It displays filter categories, options for the selected category, and action buttons.
 * Includes features like search within options, keyboard navigation, and focus trapping.
 *
 * @param {object} props - Component props.
 * @param {boolean} [props.showLoader=false] - If true, displays a loading indicator instead of the overlay content.
 * @param {Array<object>} props.options - Array of filter category objects. Each object should have `id`, `label`, `optionsType` ('checkbox' or 'radio'), `filterOptions` (array of {id, label}), and optionally `includeAllOption` (boolean), `showSearch` (boolean). Required.
 * @param {object} props.currentFilters - An object representing the currently selected filter values, keyed by filter category id. Required.
 * @param {Function} props.onApply - Callback function triggered when the Apply button is clicked. Receives an object `{ filters }` with the current filter state. Required.
 * @param {Function} [props.handleClose=()=>{}] - Callback function triggered when the Cancel button is clicked or the overlay needs to be closed.
 * @param {string} [props.testId="UIComponents-FilterOverlay"] - Base test ID for targeting elements in tests.
 * @param {Function} props.getAllFiltersCount - Function to calculate the total number of applied/changed filters (consumer defines logic). Receives `{ currentFilters }`. Required.
 * @param {Function} props.getAppliedFiltersCountForFilter - Function to calculate the number of applied/changed options within a single filter category (consumer defines logic). Receives `{ filterKey, filterValue }`. Required.
 * @param {object} [props.defaultFilters={}] - Default filters to be used for comparison. This is used to reset filters to their default state.
 *
 * @example
 * const options = [
 *   { id: 'status', label: 'Status', optionsType: 'radio', filterOptions: [{id: 'active', label: 'Active'}, {id: 'inactive', label: 'Inactive'}] },
 *   { id: 'tags', label: 'Tags', optionsType: 'checkbox', showSearch: true, filterOptions: [{id: 'tag1', label: 'Tag 1'}, {id: 'tag2', label: 'Tag 2'}] }
 * ];
 * const currentFilters = { status: 'active', tags: [] };
 *
 * <FiltersOverlay
 *   options={options}
 *   currentFilters={currentFilters}
 *   getAllFiltersCount={myCountFunction}
 *   getAppliedFiltersCountForFilter={myCategoryCountFunction}
 *   onApply={({ filters }) => console.log('Applying:', filters)}
 *   handleClose={() => console.log('Closing')}
 * />
 */
const FiltersOverlay = props => {
  const {
    showLoader,
    options,
    currentFilters,
    onApply,
    handleClose,
    testId,
    getAllFiltersCount,
    getAppliedFiltersCountForFilter,
    customContainerClass,
    defaultFilters,
  } = props;

  const containerRef = useFocusTrap({
    isActive: true, // Assuming the overlay is always active when rendered
  });

  // Robustness: Default to first option ID or null if options are empty
  const initialFilterType = options?.[0]?.id ?? null;
  const [currentFilterType, setCurrentFilterType] = useState(initialFilterType);
  const [filters, setFilters] = useState(currentFilters);

  // Recalculate derived state when filters, options or calculation functions change
  const filterOptionsWithApplyCount = useMemo(() => {
    return _.map(options, option => {
      const { id } = option;
      const filterValue = filters[id];
      const filtersAppliedCount = getAppliedFiltersCountForFilter({
        filterKey: id,
        filterValue,
      });

      return {
        ...option,
        filtersAppliedCount,
      };
    });
  }, [options, filters, getAppliedFiltersCountForFilter]);

  const totalAppliedFilters = useMemo(
    () => getAllFiltersCount({ currentFilters: filters }),
    [filters, getAllFiltersCount]
  );

  const handleChangeFilters = useCallback(
    filterValue => {
      // Only update if currentFilterType is valid
      if (currentFilterType) {
        setFilters(prevFilters => ({
          ...prevFilters,
          [currentFilterType]: filterValue,
        }));
      }
    },
    [currentFilterType]
  );

  const handleApplyFilters = useCallback(() => {
    onApply({ filters });
    handleClose(); // Close overlay after applying
  }, [filters, onApply, handleClose]);

  const resetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, [defaultFilters]);

  // Render Loading state
  if (showLoader) {
    return (
      <div className={classes.container} aria-busy="true" aria-live="polite">
        <Loading isMiniLoader />
      </div>
    );
  }

  // Render main overlay
  return (
    <DSProvider dsVersion="2.0">
      <div
        className={classNames(classes.container, customContainerClass)}
        ref={containerRef}
      >
        {/* Upper Container for Sidebar & Filter Options */}
        <div className={classes.upperContainer}>
          {/* Filters List */}
          <section className={classes.listContainer}>
            <FilterList
              options={filterOptionsWithApplyCount}
              currentFilterType={currentFilterType}
              setCurrentFilterType={setCurrentFilterType}
              testId={testId}
            />
          </section>

          {/* Filters Options */}
          <section className={classes.optionsContainer}>
            {/* Render options only if a filter type is selected */}
            <FilterOptions
              // Pass the combined options array
              filterOptionsWithApplyCount={filterOptionsWithApplyCount}
              currentFilterType={currentFilterType}
              handleChangeFilters={handleChangeFilters}
              testId={testId}
              filters={filters} // Pass current filters state
            />
          </section>
        </div>

        {/* Lower Container */}
        <section className={classes.lowerContainer}>
          <FilterActions
            totalAppliedFilters={totalAppliedFilters}
            handleApplyFilters={handleApplyFilters}
            testId={testId}
            handleClose={handleClose} // Pass the close handler
            resetFilters={resetFilters}
          />
        </section>
      </div>
    </DSProvider>
  );
};

// --- PropTypes and DefaultProps ---

const filterOptionShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.string.isRequired,
});

const filterCategoryShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  optionsType: PropTypes.oneOf(["checkbox", "radio"]),
  filterOptions: PropTypes.arrayOf(filterOptionShape).isRequired,
  includeAllOption: PropTypes.bool,
  showSearch: PropTypes.bool,
});

FiltersOverlay.propTypes = {
  /** If true, displays a loading indicator instead of the overlay content. */
  showLoader: PropTypes.bool,
  /** Array of filter category objects. */
  options: PropTypes.arrayOf(filterCategoryShape).isRequired,
  /** An object representing the currently selected filter values, keyed by filter category id. */
  currentFilters: PropTypes.object.isRequired,
  /** Callback function triggered when the Apply button is clicked. */
  onApply: PropTypes.func.isRequired,
  /** Callback function triggered when the Cancel button is clicked or the overlay needs to be closed. */
  handleClose: PropTypes.func,
  /** Base test ID for targeting elements in tests. */
  testId: PropTypes.string,
  /** Function to calculate the total number of applied/changed filters. */
  getAllFiltersCount: PropTypes.func.isRequired,
  /** Function to calculate the number of applied/changed options within a single filter category. */
  getAppliedFiltersCountForFilter: PropTypes.func.isRequired,
  /** Default filters to be used for resetting filters. */
  defaultFilters: PropTypes.object.isRequired,
};

FiltersOverlay.defaultProps = {
  showLoader: false,
  handleClose: () => {}, // Default to no-op function
  testId: "UIComponents-FilterOverlay",
};

export default FiltersOverlay;
